'use client'

import { AnimatePresence, m } from 'framer-motion'
import { ArrowUpRight, Calendar, Clock, Filter } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useMemo, useState } from 'react'

import {
  DIRECTION_FILTERS_WITH_ALL as FILTERS,
  directionFromNewsCategory,
  type DirectionId,
  type FilterId,
  formatNewsCategoryLabel,
  inferDirectionFromValues,
} from '@/shared/constants/directions'

interface Post {
  id: string
  slug: string
  title: string
  category?: string | null
  excerpt: string
  readTime?: string
  publishedAt: Date | string
  coverImage?: string | null
}

type NormalizedPost = Post & {
  category: string
  readTime: string
  dateLabel: string
  direction: DirectionId | null
}

function estimateReadTimeMinutes(text: string) {
  const words = text
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean).length
  return Math.max(3, Math.round(words / 200))
}

function normalizeCategory(category: string | null | undefined) {
  return formatNewsCategoryLabel(category)
}

function detectDirection(post: {
  category?: string | null
  title?: string
  excerpt?: string
}): DirectionId | null {
  return (
    directionFromNewsCategory(post.category) ||
    inferDirectionFromValues([post.category, post.title, post.excerpt])
  )
}

function formatRuDateUTC(d: Date | string | undefined | null) {
  if (!d) return '—'
  const date = typeof d === 'string' ? new Date(d) : d
  try {
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(date)
  } catch {
    return date.toISOString().slice(0, 10)
  }
}

export default function NewsGrid({ posts }: { posts: Post[] }) {
  const [activeFilter, setActiveFilter] = useState<FilterId>('all')
  const router = useRouter()

  const prefetchRoute = (href: string) => {
    try {
      router.prefetch(href)
    } catch {
      // no-op
    }
  }

  const normalized = useMemo<NormalizedPost[]>(() => {
    return (posts || []).map((p) => {
      const minutes = estimateReadTimeMinutes(`${p.excerpt || ''}`)
      const category = normalizeCategory(p.category)
      return {
        ...p,
        category,
        readTime: p.readTime || `${minutes} мин`,
        dateLabel: formatRuDateUTC(p.publishedAt),
        direction: detectDirection(p),
      }
    })
  }, [posts])

  const visible = useMemo(() => {
    if (activeFilter === 'all') return normalized
    return normalized.filter((p) => p.direction === activeFilter)
  }, [activeFilter, normalized])

  if (!normalized.length) {
    return (
      <section className="home-section home-container relative z-10">
        <div className="rounded-[28px] border border-white/10 bg-white/[0.03] py-16 text-center backdrop-blur-md sm:rounded-[36px] sm:py-24">
          <p className="home-subnote text-gray-400">Пока нет опубликованных новостей.</p>
        </div>
      </section>
    )
  }

  return (
    <section className="home-section home-container relative z-10 pt-0 sm:pt-2">
      <div className="mb-10 flex flex-col items-center gap-4 sm:mb-14">
        <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-gray-400">
          <Filter className="h-4 w-4" />
          <span className="home-meta text-gray-400">Фильтры</span>
        </div>
        <div className="flex w-full justify-center">
          <div className="relative isolate inline-flex w-fit max-w-full flex-wrap justify-center gap-1 rounded-[28px] border border-white/10 bg-white/5 p-2 shadow-2xl shadow-black/50 backdrop-blur-2xl sm:rounded-full">
            {FILTERS.map((filter) => {
              const isActive = activeFilter === filter.id
              return (
                <button
                  key={`news-filter-${filter.id}`}
                  type="button"
                  onClick={() => setActiveFilter(filter.id)}
                  className={[
                    'home-caption relative z-10 rounded-full px-4 py-2 font-medium transition-colors duration-300 md:px-5 md:py-2.5',
                    isActive ? 'text-black' : 'text-gray-400 hover:text-white',
                  ].join(' ')}
                >
                  {isActive && (
                    <m.div
                      layoutId="activeNewsTagFilter"
                      className="absolute inset-0 -z-10 rounded-full bg-white shadow-[0_0_20px_rgba(255,255,255,0.3)]"
                      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                    />
                  )}
                  <span className="relative z-10 font-medium tracking-wide whitespace-nowrap uppercase">
                    {filter.label}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      </div>

      <m.div layout className="grid grid-cols-1 gap-5 sm:gap-6 md:grid-cols-2 xl:grid-cols-3">
        <AnimatePresence mode="popLayout">
          {visible.map((post) => (
            <m.div
              layout
              key={post.id}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.35 }}
              className="min-w-0"
            >
              <Link
                href={`/news/${post.slug}`}
                data-testid={`news-card-${post.slug}`}
                className="group block h-full"
                onMouseEnter={() => prefetchRoute(`/news/${post.slug}`)}
                onFocus={() => prefetchRoute(`/news/${post.slug}`)}
              >
                <article className="relative flex h-full flex-col overflow-hidden rounded-[26px] border border-white/12 bg-[linear-gradient(165deg,rgba(255,255,255,0.06)_0%,rgba(255,255,255,0.02)_44%,rgba(255,255,255,0.01)_100%)] backdrop-blur-xl transition-all duration-500 contain-content hover:border-blue-300/35 hover:shadow-[0_20px_50px_rgba(20,30,80,0.35)] sm:group-hover:-translate-y-1.5">
                  <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_bottom,rgba(255,255,255,0.08),transparent_34%)]" />
                  <div className="pointer-events-none absolute -top-20 -right-16 h-52 w-52 rounded-full bg-blue-500/12 blur-[70px] will-change-transform" />
                  <div className="relative aspect-[16/9] overflow-hidden border-b border-white/10">
                    {post.coverImage ? (
                      <Image
                        src={post.coverImage}
                        alt={post.title}
                        fill
                        sizes="(min-width: 1280px) 30vw, (min-width: 768px) 46vw, 92vw"
                        className="object-cover transition-transform duration-700 sm:group-hover:scale-105"
                      />
                    ) : (
                      <div className="h-full w-full bg-gradient-to-br from-gray-900 to-black transition-colors duration-500 sm:group-hover:from-blue-900/20 sm:group-hover:to-purple-900/20" />
                    )}

                    <div className="absolute top-4 left-4">
                      <span className="home-meta inline-flex items-center rounded-full border border-white/15 bg-black/45 px-3 py-1.5 text-white/95 backdrop-blur-md">
                        {normalizeCategory(post.category)}
                      </span>
                    </div>
                  </div>

                  <div className="relative flex min-w-0 flex-grow flex-col overflow-hidden p-5 sm:p-6 lg:p-7">
                    <div className="pointer-events-none absolute top-0 right-0 h-32 w-32 rounded-full bg-blue-500/5 blur-2xl transition-colors duration-500 group-hover:bg-blue-500/10" />

                    <div className="home-meta relative z-10 mb-5 flex items-center justify-between gap-4 text-gray-500 sm:mb-6">
                      <span className="flex items-center gap-1.5 truncate">
                        <Calendar size={12} className="shrink-0 text-blue-400" />
                        {post.dateLabel ?? formatRuDateUTC(post.publishedAt)}
                      </span>
                      <span className="flex shrink-0 items-center gap-1.5">
                        <Clock size={12} />
                        {post.readTime}
                      </span>
                    </div>

                    <h3 className="home-card-title-sm relative z-10 mb-3 line-clamp-2 leading-snug break-words transition-colors group-hover:text-blue-400 sm:mb-4">
                      {post.title}
                    </h3>

                    <p className="home-subnote relative z-10 mb-6 line-clamp-2 flex-grow leading-relaxed text-gray-400 sm:mb-7">
                      {post.excerpt}
                    </p>

                    <div className="relative z-10 flex items-center justify-between border-t border-white/10 pt-5 sm:pt-6">
                      <span className="home-meta text-gray-100 transition-colors group-hover:text-blue-300">
                        Читать
                      </span>
                      <div className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-white/[0.03] transition-all duration-300 group-hover:border-blue-400 group-hover:bg-blue-500">
                        <ArrowUpRight size={14} className="text-gray-300 group-hover:text-white" />
                      </div>
                    </div>
                  </div>
                </article>
              </Link>
            </m.div>
          ))}
        </AnimatePresence>
      </m.div>
    </section>
  )
}
